import type { ChainId } from '@uniswap/sdk-core';
import { getChainInfo } from 'constants/chainInfo';

import { cn } from 'utils/cn';
import { useFormatter } from 'utils/formatNumbers';
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink';

import { TransactionStatus } from 'graphql/data/__generated__/types-and-hooks';
import { useAllTokensMultichain } from 'hooks/Tokens';
import useENSName from 'hooks/useENSName';
import { usePlatformToken } from 'hooks/usePlatformToken';
import { useTransaction } from 'state/transactions/hooks';

import {
  PrimaryLargeText,
  SecondaryLargeText,
  SecondaryText
} from 'components/ui/Text';

import { transactionToActivity } from 'components/AccountDrawer/MiniPortfolio/Activity/parseLocal';
import type { Activity } from 'components/AccountDrawer/MiniPortfolio/Activity/types';
import { PortfolioLogo } from 'components/AccountDrawer/MiniPortfolio/PortfolioLogo';
import PortfolioRow from 'components/AccountDrawer/MiniPortfolio/PortfolioRow';
import { CloseIcon, TriangleIcon } from 'components/Icons';

type PopupWrapperProps = {
  children: React.ReactNode;
  className?: string;
};

const PopupWrapper = ({ children, className }: PopupWrapperProps) => (
  <div
    className={cn(
      'border border-solid border-blue-700 bg-blue-900',
      'min-w-72.5 relative inline-block w-full overflow-hidden rounded-2xl px-0 py-0.5 lg:shadow-lg lg:shadow-blue-800/60',
      className
    )}
  >
    {children}
  </div>
);

export const FailedNetworkSwitchPopup = ({
  chainId,
  onClose
}: {
  chainId: ChainId;
  onClose: () => void;
}) => {
  const chainInfo = getChainInfo(chainId);

  return (
    <PopupWrapper className='py-5 pl-8 pr-5'>
      <CloseIcon
        className='absolute right-4 top-4 size-4 stroke-gray-600'
        onClick={onClose}
      />

      <div className='flex w-full flex-nowrap items-center justify-start gap-3'>
        <TriangleIcon className='size-8 flex-shrink-0 text-yellow' />

        <div className='mx-3 grid auto-rows-auto gap-y-2'>
          <SecondaryLargeText>Failed to switch networks</SecondaryLargeText>

          <SecondaryText>
            To use the app on {chainInfo.label}, switch the network in your
            wallet’s settings.
          </SecondaryText>
        </div>
      </div>
    </PopupWrapper>
  );
};

type ActivityPopupContentProps = {
  activity: Activity;
  onClick: () => void;
  onClose: () => void;
};
const ActivityPopupContent = ({
  activity,
  onClick,
  onClose
}: ActivityPopupContentProps) => {
  const success =
    activity.status === TransactionStatus.Confirmed && !activity.cancelled;
  const { ENSName } = useENSName(activity?.otherAccount);

  return (
    <PopupWrapper>
      <CloseIcon
        className='absolute right-4 top-4 size-4 stroke-gray-600'
        onClick={onClose}
      />

      <PortfolioRow
        left={
          success ? (
            <div className='flex flex-col justify-start'>
              <PortfolioLogo
                chainId={activity.chainId}
                currencies={activity.currencies}
                images={activity.logos}
                accountAddress={activity.otherAccount}
              />
            </div>
          ) : (
            <TriangleIcon className='size-8 flex-shrink-0 text-yellow' />
          )
        }
        title={<PrimaryLargeText>{activity.title}</PrimaryLargeText>}
        descriptor={
          <SecondaryText className='overflow-hidden text-ellipsis whitespace-nowrap'>
            {activity.descriptor}
            {ENSName ?? activity.otherAccount}
          </SecondaryText>
        }
        onClick={onClick}
      />
    </PopupWrapper>
  );
};

export const TransactionPopupContent = ({
  chainId,
  hash,
  onClose
}: {
  chainId: ChainId;
  hash: string;
  onClose: () => void;
}) => {
  const transaction = useTransaction(hash);
  const tokens = useAllTokensMultichain();
  const platformToken = usePlatformToken();
  const { formatNumber } = useFormatter();
  if (!transaction) return null;

  const activity = transactionToActivity(
    transaction,
    chainId,
    tokens,
    formatNumber,
    platformToken
  );

  if (!activity) return null;

  const onClick = () =>
    window.open(
      getExplorerLink({
        chainId: activity.chainId,
        data: activity.hash,
        type: ExplorerDataType.TRANSACTION
      }),
      '_blank'
    );

  return (
    <ActivityPopupContent
      activity={activity}
      onClose={onClose}
      onClick={onClick}
    />
  );
};
